/**
 * useShellAreaRuling - "who is caught?", asked of the table, around the attack.
 *
 * While positions count this is inert: the map answers. While they do not:
 *
 * - `fire(attack)` asks BEFORE an area weapon is fired - the attacker ticks
 *   who is caught - and only then runs the attack, with the answer where the
 *   hand-ruled resolver (`useShellAreaByHand`) reads it.
 * - `parkScatter(info, resume)` is handed to the attack: a blast that
 *   misses and scatters is ruled on the same way, and its detonation
 *   resumes when the ruling is in. "Nobody" is a ruling.
 *
 * WHO RULES. The GM, and anyone outside a session, rules alone. A player's
 * ticks are a PROPOSAL: they go to the GM as an ask, the GM confirms or
 * corrects them on the same list, and the attack waits. A withdrawn or
 * dismissed ask is "do not fire" for an aimed shot and "nobody" for a
 * scatter (the miss has already happened).
 *
 * @param {object} args - positionsCount, rulesAlone, roster, actor, activeTab,
 *   slot (the armed weapon as fired), attackMode, blastSelfPolicy,
 *   ticksRef (from useShellAreaByHand), ask (from useAsks).
 * @returns {{ request, submit, cancel, fire, parkScatter }}
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { areaAskOf, needsTicks } from '../engine/phase/areaAsk';
import { NO_TICKS, readTicks } from '../engine/phase/areaTicks';

/** The ask's kind on the wire. */
export const AREA_ASK = 'area';

/**
 * The question itself: put it on screen (`rule`), and the two ways it ends
 * (`submit`, `cancel`). `live` is the hook's arguments, read at call time: a
 * scatter is ruled on seconds after the render that fired it.
 */
function useAreaQuestion(live, { rulesAlone, ask }) {
  /** The question on this screen: `{ ask, mode }`, or null. Its promise is kept beside it, not in state. */
  const [request, setRequest] = useState(null);
  const pending = useRef(null);
  const shown = useRef(null);
  shown.current = request;

  /** Take the pending promise's resolver, leaving none - so a ruling is delivered once. */
  const take = useCallback(() => {
    const resolve = pending.current;
    pending.current = null;
    setRequest(null);
    return resolve;
  }, []);

  /** Put the question on screen; resolves with the ruling, or null when nobody ruled. */
  const rule = useCallback(
    (scatter, slot) =>
      new Promise((resolve) => {
        const now = live.current;
        const areaAsk = areaAskOf({
          roster: now.roster,
          actor: now.actor,
          slot,
          suppressing: now.attackMode === 'suppressing',
          aimedId: now.activeTab,
          scatter,
          blastSelfPolicy: now.blastSelfPolicy,
        });
        // One question at a time: a second shot while one is open withdraws the first.
        pending.current?.(null);
        pending.current = resolve;
        setRequest({ ask: areaAsk, mode: rulesAlone ? 'fire' : 'propose' });
      }),
    [rulesAlone, live],
  );

  const submit = useCallback(
    (ticks) => {
      const asked = shown.current;
      const resolve = take();
      if (!resolve) return;
      if (rulesAlone) return resolve(ticks);
      ask(AREA_ASK, 'gm', { ...asked.ask, ticks }).then((answer) => resolve(answer ? readTicks(answer) : null));
    },
    [rulesAlone, ask, take],
  );

  const cancel = useCallback(() => take()?.(null), [take]);
  return { request, rule, submit, cancel };
}

export default function useShellAreaRuling(args) {
  const { positionsCount, rulesAlone, ticksRef, ask } = args;
  const live = useRef(args);
  live.current = args;
  const { request, rule, submit, cancel } = useAreaQuestion(live, { rulesAlone, ask });

  /** Run `attack`, asking first when this shot needs a tick list. */
  const fire = useCallback(
    (attack) => {
      const now = live.current;
      const asks = !positionsCount && !!now.activeTab && needsTicks(now.slot, now.attackMode === 'suppressing');
      if (!asks) return attack();
      rule(null, now.slot).then((ticks) => {
        if (!ticks) return;
        ticksRef.current = ticks;
        attack();
      });
    },
    [positionsCount, rule, ticksRef],
  );

  const parkScatter = useMemo(() => {
    if (positionsCount) return null;
    return (info, resume) =>
      rule(info.scatter, info.slot).then((ticks) => {
        ticksRef.current = ticks ?? NO_TICKS;
        resume();
      });
  }, [positionsCount, rule, ticksRef]);

  return { request, submit, cancel, fire, parkScatter };
}
